import React, { useEffect, useState } from 'react'
import CustomIcons from '../../../ui_reusable/CustomIcons'

import { GhostIconButtonRed } from '../../../ui_reusable/styles/Button.styles'
import { StandardToggleButton } from '../../../ui_reusable/styles/ToggleButton.styles'
import {
  WarningSection,
  OptionsSection,
  TwoColumnMainContainer, // Changed from ThreeColumnContainer
  Column,
  TwoColumnContainer,
  ControlColumn,
  ContentColumn,
} from '../../../ui_reusable/styles/Deactivate.styles'
import {
  HeaderLargeImportant,
  TextMediumMaxi600Style,
  HeaderMedium600Style,
  TextSmall500FadedStyle,
} from '../../../../styles/Text.styles'
import Modal, { ModalDefaultButtonFooter } from '../../../ui_reusable/Modal'
import { Message } from 'rsuite'
import useStandardToaster from '../../../ui_reusable/StandardToaster'
import { useNavigate } from 'react-router-dom'
import { useClientProjectContext } from '../../../../context/ClientProjectContext'
import { useProjectContext } from '../../../../context/ProjectContext'

const ClientDeactivate = ({ client, setRefresh }) => {
  const [isActive, setIsActive] = useState(true) // This will come from API
  const [showDeactivateModal, setShowDeactivateModal] = useState(false)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [showDeleteUnderstandModal, setShowDeleteUnderstandModal] =
    useState(false)
  const { pushToast } = useStandardToaster()
  const navigate = useNavigate()
  const { refreshClientProjects } = useClientProjectContext()
  const { selectedProject, setSelectedProject } = useProjectContext()

  useEffect(() => {
    setIsActive(client.is_active)
  }, [client])

  const updateClientStatus = async (activate = true) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/clients/${client.id}`,
        {
          method: 'PATCH',
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ is_active: activate }),
        },
      )

      if (response.ok) {
        setIsActive(activate)
        setRefresh(new Date().getTime())
        if (
          !activate &&
          Number(selectedProject?.client_id) === Number(client.id)
        ) {
          setSelectedProject({ ...selectedProject, status: 'inactive' })
        }
        await refreshClientProjects()
        pushToast(
          <Message type="success">
            {activate ? 'Client is activated' : 'Client is deactivated'}
          </Message>,
          { placement: 'topCenter', duration: 5000 },
        )
      } else {
        throw new Error('Failed to update client status')
      }
    } catch (error) {
      pushToast(
        <Message type="error">An error occurred, try again later.</Message>,
        { placement: 'topCenter', duration: 5000 },
      )
    }
  }

  const handleActiveToggle = async () => {
    if (isActive) {
      setShowDeactivateModal(true)
    } else {
      await updateClientStatus(true)
    }
  }

  const handleDeleteClick = () => {
    setShowDeleteUnderstandModal(true)
  }

  const handleUnderstandClick = async () => {
    setShowDeleteUnderstandModal(false)
    setShowDeleteModal(true)
  }

  const handleDeleteClient = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/clients/${client.id}`,
        {
          method: 'DELETE',
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json',
          },
        },
      )

      if (response.ok) {
        setShowDeleteModal(false)
        pushToast(<Message type="success">Client is deleted</Message>, {
          placement: 'topCenter',
          duration: 5000,
        })
        await refreshClientProjects()
        navigate('/clients')
      } else {
        throw new Error('Failed to delete client')
      }
    } catch (error) {
      pushToast(
        <Message type="error">An error occurred, try again later.</Message>,
        { placement: 'topCenter', duration: 5000 },
      )
    }
  }

  return (
    <>
      <WarningSection>
        <h2 css={HeaderLargeImportant}>DANGER ZONE</h2>
        <span css={TextMediumMaxi600Style}>
          Please be very careful and very sure before making changes here.
        </span>
      </WarningSection>

      <OptionsSection>
        <TwoColumnMainContainer>
          {' '}
          {/* Changed from ThreeColumnContainer */}
          {/* Active Status Column */}
          <Column>
            <TwoColumnContainer>
              <ControlColumn>
                <StandardToggleButton
                  checked={isActive}
                  onChange={handleActiveToggle}
                  data-testid="deactivate-client"
                />
              </ControlColumn>
              <ContentColumn>
                <h4
                  css={HeaderMedium600Style}
                  style={{ color: isActive ? '#c5841c' : '#A0A0A0' }}
                >
                  Active status
                </h4>
                <span
                  css={TextSmall500FadedStyle}
                  style={{ color: isActive ? '#c5841c' : '#A0A0A0' }}
                >
                  Click to deactivate client from Citrus.
                  <br />
                  This can be reverted later.
                </span>
              </ContentColumn>
            </TwoColumnContainer>
          </Column>
          {/* Delete Column */}
          <Column>
            <TwoColumnContainer>
              <ControlColumn>
                <GhostIconButtonRed
                  onClick={handleDeleteClick}
                  data-testid="delete-client"
                >
                  <CustomIcons.TrashIcon size="xs" color="#ff0000" />
                </GhostIconButtonRed>
              </ControlColumn>
              <ContentColumn>
                <h4 css={HeaderMedium600Style} style={{ color: '#b71c1c' }}>
                  Delete client
                </h4>
                <span css={TextSmall500FadedStyle} style={{ color: '#b71c1c' }}>
                  Click to delete client from Citrus. This action cannot be
                  undone.
                </span>
              </ContentColumn>
            </TwoColumnContainer>
          </Column>
        </TwoColumnMainContainer>
      </OptionsSection>

      {/* Deactivate Modal */}
      <Modal
        open={showDeactivateModal}
        onClose={() => setShowDeactivateModal(false)}
      >
        <Modal.Header onClose={() => setShowDeactivateModal(false)}>
          <Modal.Title>Deactivate Client?</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to deactivate this client? The client with all
          its users will lose access to all models unless the client is
          activated again.
        </Modal.Body>
        <Modal.Footer>
          {ModalDefaultButtonFooter({
            confirmText: 'Deactivate',
            cancelText: 'Cancel',
            confirmColor: '#f5a623',
            onConfirm: async () => {
              await updateClientStatus(false)
              setShowDeactivateModal(false)
            },
            onCancel: () => setShowDeactivateModal(false),
          })}
        </Modal.Footer>
      </Modal>

      {/* Delete Understanding Modal */}
      <Modal
        open={showDeleteUnderstandModal}
        onClose={() => setShowDeleteUnderstandModal(false)}
      >
        <Modal.Header onClose={() => setShowDeleteUnderstandModal(false)}>
          <Modal.Title>Do you understand?</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Hold on. You are about to delete a client from Citrus. Do you
          understand that this action cannot be undone?
        </Modal.Body>
        <Modal.Footer>
          {ModalDefaultButtonFooter({
            confirmText: 'I understand',
            cancelText: 'Take me back',
            confirmColor: '#b71c1c',
            onConfirm: handleUnderstandClick,
            onCancel: () => setShowDeleteUnderstandModal(false),
            flip: true,
          })}
        </Modal.Footer>
      </Modal>

      {/* Delete Modal */}
      <Modal open={showDeleteModal} onClose={() => setShowDeleteModal(false)}>
        <Modal.Header onClose={() => setShowDeleteModal(false)}>
          <Modal.Title style={{ color: '#b71c1c' }}>Delete Client?</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div>
            Do you want to delete the client? The client with all its users will
            lose access to all models. Are you sure about this?
          </div>
        </Modal.Body>
        <Modal.Footer>
          {ModalDefaultButtonFooter({
            confirmText: 'Delete client',
            cancelText: 'Take me back',
            confirmColor: '#b71c1c',
            onConfirm: () => handleDeleteClient(),
            onCancel: () => setShowDeleteModal(false),
          })}
        </Modal.Footer>
      </Modal>
    </>
  )
}

export default ClientDeactivate
